/**
 * Step details: the step header shows only the product; the batch and the
 * rest of the order show on hover (tooltip) and on click (popover).
 */
import { Button, Popover, Tooltip } from "@mui/material";
import React, { useCallback, useState } from "react";

import { hhmm } from "../../../utils/time";
import { LaneStep } from "../utils/lane-rows";
import { fmtHours, lbb } from "../utils/tokens";

const StepFacts: React.FC<{ step: LaneStep }> = ({ step }) => {
	const rows = [
		...step.details,
		{ label: "Runs", value: `${hhmm(step.start)} – ${hhmm(step.end)} · ${fmtHours(step.end - step.start)}` },
		{
			label: "Crew",
			value: `${step.crewHave} of ${step.crewNeeded} filled${step.crewOut > 0 ? ` · ${step.crewOut} out` : ""}`,
		},
	];
	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 4, fontFamily: lbb.fontUi, fontSize: 12 }}>
			<div style={{ fontWeight: 700, fontSize: 13 }}>{step.title}</div>
			<dl style={{ margin: 0, display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 10, rowGap: 2 }}>
				{rows.map((row) => (
					<React.Fragment key={row.label}>
						<dt style={{ opacity: 0.7 }}>{row.label}</dt>
						<dd style={{ margin: 0, fontFamily: lbb.fontMono }}>{row.value}</dd>
					</React.Fragment>
				))}
			</dl>
		</div>
	);
};

/**
 * Wraps a step header: hover shows the details, click opens them in a
 * popover (with "Open day view" when `onOpenDay` is given).
 */
export const StepDetails: React.FC<{
	step: LaneStep;
	onOpenDay?: () => void;
	openDayLabel?: string;
	children: (handleClick: (event: React.MouseEvent<HTMLElement>) => void) => React.ReactElement;
}> = ({ step, onOpenDay, openDayLabel, children }) => {
	const [anchor, setAnchor] = useState<HTMLElement | null>(null);
	const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
		event.stopPropagation();
		setAnchor(event.currentTarget);
	}, []);
	const handleClose = useCallback(() => setAnchor(null), []);
	const handleOpenDay = useCallback(() => {
		setAnchor(null);
		onOpenDay?.();
	}, [onOpenDay]);
	return (
		<>
			<Tooltip title={anchor ? "" : <StepFacts step={step} />} placement="top-start" enterDelay={400} disableInteractive>
				{children(handleClick)}
			</Tooltip>
			<Popover
				open={Boolean(anchor)}
				anchorEl={anchor}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				slotProps={{ paper: { sx: { p: 1.5, maxWidth: 320, color: lbb.ink } } }}
			>
				<StepFacts step={step} />
				{onOpenDay && (
					<Button size="small" onClick={handleOpenDay} sx={{ mt: 1, px: 0, color: lbb.brick, textTransform: "none" }}>
						{openDayLabel ?? "Open day view"}
					</Button>
				)}
			</Popover>
		</>
	);
};
